import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Button,
    Typography,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    FormControlLabel,
    Switch
} from "@mui/material";
import CustomTimePicker from "../user/components/CustomTimePicker";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { DialogTheme } from "../styles/DialogTheme";

export const LayoutDialog = ({
    openLayoutDialog,
    onClose,
    defaultCellDuration,
    onCellDurationChange,
    defaultExcludedDays,
    onExcludedDaysChange,
    defaultStartTimeBoundary,
    defaultEndTimeBoundary,
    onStartEndTimeChange
}) => {
    const convertHourToTimestamp = (hour) => {
        const timestamp = new Date();
        if (hour === 24) {
            timestamp.setHours(23, 59, 0, 0);
        } else {
            timestamp.setHours(hour, 0, 0, 0);
        }
        return timestamp.getTime();
    };

    const [showSaturdays, setShowSaturdays] = useState(
        defaultExcludedDays.includes(6) ? false : true
    );
    const [showSundays, setShowSundays] = useState(
        defaultExcludedDays.includes(0) ? false : true
    );
    const [startTimeBoundary, setStartTimeBoundary] = useState(
        defaultStartTimeBoundary !== null &&
            defaultStartTimeBoundary !== undefined
            ? convertHourToTimestamp(defaultStartTimeBoundary)
            : false
    );
    const [endTimeBoundary, setEndTimeBoundary] = useState(
        defaultEndTimeBoundary !== null && defaultEndTimeBoundary !== undefined
            ? convertHourToTimestamp(defaultEndTimeBoundary)
            : false
    );
    const [cellDuration, setCellDuration] = useState(defaultCellDuration || 60);
    const [excludedDays, setExcludedDays] = useState(defaultExcludedDays || []);
    const [disableUpdate, setDisableUpdate] = useState(true);
    const [error, setError] = useState(false);

    const theme = useTheme();

    // Save calendar settings to localStorage
    const saveToLocalStorage = () => {
        const settings = {
            cellDuration,
            startDayHour: startTimeBoundary
                ? new Date(startTimeBoundary).getHours() +
                  (new Date(startTimeBoundary).getMinutes() === 59 ? 1 : 0)
                : 7,
            endDayHour: endTimeBoundary
                ? new Date(endTimeBoundary).getHours() +
                  (new Date(endTimeBoundary).getMinutes() === 59 ? 1 : 0)
                : 19,
            excludedDays
        };
        localStorage.setItem("calendarSettings", JSON.stringify(settings));
    };

    const handleCellDurationChange = (e) => {
        const newDuration = e.target.value;
        setCellDuration(newDuration);
        setDisableUpdate(false);
    };

    const handleShowSaturdaysChange = (event) => {
        const newValue = event.target.checked;
        setShowSaturdays(newValue);
        updateExcludedDays(newValue, 6);
        setDisableUpdate(false);
    };

    const handleShowSundaysChange = (event) => {
        const newValue = event.target.checked;
        setShowSundays(newValue);
        updateExcludedDays(newValue, 0);
        setDisableUpdate(false);
    };

    const updateExcludedDays = (showDay, dayIndex) => {
        setExcludedDays((prevExcludedDays) => {
            let updatedExcludedDays = [...prevExcludedDays];
            if (!showDay && !updatedExcludedDays.includes(dayIndex)) {
                updatedExcludedDays.push(dayIndex); // If the day is not shown, add to the array
            } else if (showDay && updatedExcludedDays.includes(dayIndex)) {
                updatedExcludedDays = updatedExcludedDays.filter(
                    (day) => day !== dayIndex
                ); // If shown, remove from the array
            }
            return updatedExcludedDays;
        });
        setDisableUpdate(false);
    };

    const handleDoneClick = () => {
        setError(false);

        // Handle validation of time boundaries
        const startHour = startTimeBoundary
            ? new Date(startTimeBoundary).getHours() +
              (new Date(startTimeBoundary).getMinutes() === 59 ? 1 : 0) //(eg. 23:59 == 24)
            : 7;
        const endHour = endTimeBoundary
            ? new Date(endTimeBoundary).getHours() +
              (new Date(endTimeBoundary).getMinutes() === 59 ? 1 : 0)
            : 19;

        if (endHour <= startHour) {
            setError({
                type: "required",
                message: "End time must be after start time"
            });
            return;
        }

        if (onCellDurationChange) {
            onCellDurationChange(cellDuration);
        }

        if (onExcludedDaysChange) {
            onExcludedDaysChange(excludedDays);
        }

        if (onStartEndTimeChange) {
            onStartEndTimeChange(startHour, endHour); // Pass the start and end hours as whole integers
        }

        saveToLocalStorage();
        onClose();
    };

    return (
        <ThemeProvider theme={DialogTheme(theme)}>
            <Dialog open={openLayoutDialog} onClose={onClose}>
                <DialogTitle
                    id="calendar-layout-dialog-title"
                    sx={{ paddingBottom: "18px" }}
                >
                    Calendar layout
                </DialogTitle>
                <DialogContent>
                    <Grid container gap="18px">
                        <Grid item xs={12} sm={12} sx={{ paddingTop: "14px" }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showSaturdays}
                                        onChange={handleShowSaturdaysChange}
                                        name={"showSaturdays"}
                                        color="primary"
                                        inputProps={{
                                            "aria-label": "primary checkbox"
                                        }}
                                    />
                                }
                                label="Show Saturdays"
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name={"showSundays"}
                                        color="primary"
                                        checked={showSundays}
                                        onChange={handleShowSundaysChange}
                                        inputProps={{
                                            "aria-label": "primary checkbox"
                                        }}
                                    />
                                }
                                label="Show Sundays"
                            />
                        </Grid>
                        <Grid
                            container
                            direction={"row"}
                            gap="18px"
                            sx={{ paddingTop: "3px" }}
                        >
                            <Grid item xs={6} sm={5.7}>
                                <CustomTimePicker
                                    id="start-time-boundary"
                                    type="startTimeBoundary"
                                    value={startTimeBoundary}
                                    step={60}
                                    onChange={(startTimeBoundary) => {
                                        setError(false);
                                        setStartTimeBoundary(startTimeBoundary);
                                        setDisableUpdate(false);
                                    }}
                                    error={error}
                                />
                                {error && (
                                    <Grid
                                        sx={{
                                            minHeight: "21px"
                                        }}
                                    >
                                        <Typography> </Typography>
                                    </Grid>
                                )}
                            </Grid>
                            <Grid item xs={6} sm={5.7}>
                                <CustomTimePicker
                                    id="end-time-boundary"
                                    type="endTimeBoundary"
                                    value={endTimeBoundary}
                                    step={60}
                                    onChange={(endTimeBoundary) => {
                                        setError(false);
                                        setEndTimeBoundary(endTimeBoundary);
                                        setDisableUpdate(false);
                                    }}
                                    error={error}
                                />
                                {error && (
                                    <Grid>
                                        <Typography variant="errorText">
                                            {error.message}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                        <Grid item xs={6} sm={6} sx={{ paddingRight: "10px" }}>
                            <FormControl fullWidth>
                                <InputLabel id="resolution">
                                    Cell duration
                                </InputLabel>
                                <Select
                                    label="Cell duration"
                                    labelId="cell-duration"
                                    value={cellDuration}
                                    onChange={handleCellDurationChange}
                                    sx={{
                                        "&.MuiInputLabel-root": {
                                            display: "none"
                                        }
                                    }}
                                >
                                    <MenuItem
                                        value={60}
                                        key={"60"}
                                        sx={{ fontSize: "14px" }}
                                    >
                                        60 mins
                                    </MenuItem>
                                    <MenuItem
                                        value={30}
                                        key={"30"}
                                        sx={{ fontSize: "14px" }}
                                    >
                                        30 mins
                                    </MenuItem>
                                    <MenuItem
                                        value={15}
                                        key={"15"}
                                        sx={{ fontSize: "14px" }}
                                    >
                                        15 mins
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Grid
                        container
                        item
                        gap={"18px"}
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end"
                        }}
                    >
                        <Button variant="cancelButton" onClick={onClose}>
                            CANCEL
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleDoneClick}
                            disabled={disableUpdate}
                        >
                            UPDATE LAYOUT
                        </Button>
                    </Grid>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
};
